import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import silverUnicorn from '../../assets/getfunded/silver-unicorn.png';

const CARD_BG = '#1E1E1C';
const STAR_ON = '#92d050';
const STAR_OFF = '#696969';
const QUESTION_GREEN = '#92d050';
const SKIP_BLUE = '#2B9FEF';

interface RateRegistrationPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

/** First dashboard visit after profile complete: sign-up rating (SUCCESS + review). */
const RateRegistrationPopup: React.FC<RateRegistrationPopupProps> = ({ isOpen, onClose }) => {
  const { user, refreshUser } = useAuth();
  const [stars, setStars] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!isOpen) return;
    setStars(0);
    setFeedback('');
    setError('');
  }, [isOpen]);

  const save = async (options: { requireStars: boolean; starsOverride?: number }) => {
    if (!user?.id || busy) return;
    const starsToSave = options.starsOverride ?? stars;
    if (options.requireStars && starsToSave < 1) {
      setError('Please choose a star rating.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const updateUser = httpsCallable(functions, 'updateUser');
      const result = await updateUser({
        userId: user.id,
        updates: {
          rating: {
            stars: starsToSave,
            feedback: options.starsOverride === 0 ? '' : feedback.trim(),
          },
        },
      });
      const data = result.data as { success?: boolean; error?: string };
      if (data?.success === false) {
        // Already submitted once — treat as done so the popup does not stick.
        if (/already submitted/i.test(data.error || '')) {
          onClose();
          return;
        }
        throw new Error(data.error || 'Could not save your rating.');
      }
      try {
        await refreshUser();
      } catch {
        // The rating is already stored.
      }
      // Close immediately so the next profile popup can show (no unicorn-only interstitial).
      onClose();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Could not save your rating.';
      // Deployed function may reject a second submit as already done.
      if (/already submitted/i.test(message)) {
        onClose();
        return;
      }
      setError(message.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setBusy(false);
    }
  };

  const dismissAsSkip = () => {
    if (busy) return;
    void save({ requireStars: false, starsOverride: 0 });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || busy) return;
      void save({ requireStars: false, starsOverride: 0 });
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, busy]); // eslint-disable-line react-hooks/exhaustive-deps -- save closes over latest state

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/15 flex items-center justify-center z-[100000] p-4"
      onClick={event => {
        if (event.target === event.currentTarget) dismissAsSkip();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rate-registration-title"
    >
      <div
        className="relative border border-gray-700 rounded-2xl shadow-xl max-w-md w-full px-8 py-8 text-center"
        style={{ backgroundColor: CARD_BG, fontFamily: 'Rubik, sans-serif' }}
        onClick={event => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={dismissAsSkip}
          className="absolute right-3 top-3 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <img src={silverUnicorn} alt="" className="mx-auto mb-3 h-12 w-auto" />
        <h2 id="rate-registration-title" className="text-white text-2xl font-bold tracking-wide">
          SUCCESS!
        </h2>
        <p className="text-white text-sm sm:text-base mt-2 mb-5">Your profile is now ready.</p>
        <div className="border-t border-gray-700 mb-5" />
        <p className="text-base sm:text-lg font-medium mb-4" style={{ color: QUESTION_GREEN }}>
          How was the sign-up process?
        </p>
        <div
          className="flex items-center justify-center gap-2"
          role="radiogroup"
          aria-label="Rating"
        >
          {[1, 2, 3, 4, 5].map(value => (
            <button
              key={value}
              type="button"
              disabled={busy}
              onClick={() => {
                setStars(value);
                setError('');
              }}
              className="text-3xl leading-none disabled:opacity-60"
              style={{ color: value <= stars ? STAR_ON : STAR_OFF }}
              aria-label={`${value} star${value === 1 ? '' : 's'}`}
              aria-checked={stars === value}
              role="radio"
            >
              ★
            </button>
          ))}
        </div>
        <p className="text-white text-base sm:text-lg mt-5 mb-3">Anything we missed?</p>
        <input
          type="text"
          value={feedback}
          onChange={event => setFeedback(event.target.value)}
          disabled={busy}
          className="w-full max-w-[16rem] mx-auto block rounded-md px-3 py-1.5 text-sm text-gray-900 bg-white outline-none"
        />
        {error ? <p className="text-red-500 text-sm mt-3">{error}</p> : null}
        <div className="mt-5 flex items-end justify-center gap-4">
          <button
            type="button"
            onClick={() => void save({ requireStars: true })}
            disabled={busy}
            className="rounded-lg bg-emerald-700 px-5 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-60"
          >
            {busy ? 'Sending…' : 'Send'}
          </button>
          <button
            type="button"
            onClick={() => void save({ requireStars: false })}
            disabled={busy}
            className="pb-2 text-sm underline disabled:opacity-60"
            style={{ color: SKIP_BLUE }}
          >
            Skip
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RateRegistrationPopup;
